/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import BrowserDetector from '../../dom/BrowserDetector';
import assertUnreachable from '../../lang/assertUnreachable';
import {AudioCodec} from '../decode/bitstream/codec/AudioCodec';
import {VideoCodec} from '../decode/bitstream/codec/VideoCodec';
import annexBFourByteStartCode from '../decode/bitstream/codec/h264/nalUnit/AnnexBFourByteStartCode';
import surrogateH26428x16IFrame from './surrogateFrames/h264/surrogate28x16IFrame';
import surrogateH26428x16PFrame from './surrogateFrames/h264/surrogate28x16PFrame';

export const SkipFrame = Symbol();

export type SurrogateFrameType = ArrayBuffer | undefined | typeof SkipFrame;

export default class SurrogateFrameDataManager {
  private static readonly _isSafari = BrowserDetector.browserName === 'Safari';
  private static readonly _isAndroidChrome = BrowserDetector.browserName === 'Chrome' && BrowserDetector.isAndroid;
  private static readonly _singleZeroByte = new Uint8Array([0x00]);
  private static readonly _dummyAudioSilentPerCodec = {OPUS: new Uint8Array([0x68, 0x07, 0xd9, 0xab, 0xbc, 0x79, 0x87, 0xab, 0x8e, 0x4a, 0x14, 0xf3, 0xa7, 0x1c, 0xf2, 0x9a, 0x7a, 0x45, 0xa7, 0x2d, 0x44, 0x25, 0x74, 0x4c, 0xad, 0x16, 0xb3, 0x0e, 0x51, 0x57, 0xea, 0xe7, 0xad, 0x2a, 0x38, 0xce, 0x33])};
  private static readonly _dummy2x2VideoIFramePerCodec = {
    H264: new Uint8Array([
      ...annexBFourByteStartCode, 0x09, 0xf0, // Access unit delimiter
      ...annexBFourByteStartCode, 0x67, 0xf4, 0x00, 0x0a, 0x91, 0x9b, 0x2b, 0xf1, 0xf1, 0xf8, 0x08, 0x80, 0x00, 0x00, 0x03, 0x00, 0x80, 0x00, 0x00, 0x19, 0x07, 0x89, 0x12, 0xcb, // Sequence parameter set
      ...annexBFourByteStartCode, 0x68, 0xeb, 0xe3, 0xc4, 0x48, 0x44, // Picture paramter set
      /* */ 0x00, 0x00, 0x01, 0x65, 0x88, 0x82, 0x00, 0x3f, 0x70, 0x23, 0x86, 0xe6, 0xd1, 0x80, 0x09, 0xaf, 0x0a, 0x41, 0x66, 0x6b, 0x54, 0x46, 0xfb, 0x71, 0xd9, 0x7f, 0xff, 0xff, 0xf9]), // Slice layer: idr data
    VP8: new Uint8Array([0xb0, 0x02, 0x00, 0x9d, 0x01, 0x2a, 0x01, 0x00, 0x01, 0x00, 0x39, 0x6b, 0x00, 0x27, 0x1c, 0x24, 0x0c, 0x2c, 0x2c, 0x44, 0xcc, 0x24, 0x41, 0xa8, 0x00, 0x13, 0xa7, 0xa3, 0x7a, 0x50, 0x00, 0xfe, 0xeb, 0xde, 0x2f, 0x8a, 0x1e, 0x6b, 0x7b, 0x07, 0x72, 0x63, 0x09, 0xa4, 0x8e, 0xc0]),
    VP9: new Uint8Array([0x82, 0x49, 0x83, 0x42, 0x00, 0x00, 0x00, 0x00, 0x06, 0x76, 0x38, 0x24, 0x1c, 0x19, 0xa0, 0x00, 0x00, 0x20, 0x40, 0x00, 0x11, 0xbf, 0xdd, 0x77, 0xff, 0x40, 0x7f, 0xb1, 0x00])
  };
  private static readonly _dummy2x2VideoPFramePerCodec = {
    H264: new Uint8Array([
      ...annexBFourByteStartCode, 0x09, 0xf0, // Access unit delimiter
      ...annexBFourByteStartCode, 0x41, 0x9a, 0x68, 0x49, 0xa8, 0x41, 0x68, 0x99, 0x4c, 0x0f, 0xff, 0x92, 0x81]), // Slice layer: non idr data
    VP8: new Uint8Array([0x31, 0x03, 0x00, 0xe4, 0xe0, 0xa8, 0x9b, 0x37, 0xaf, 0x10, 0x4f, 0x4f, 0xb1, 0x0a, 0x2f, 0xe4, 0x92, 0xc0, 0x1f, 0xe7, 0x4f, 0x46, 0xff, 0xee, 0x51, 0xc1, 0x5d, 0x00, 0xfe, 0xeb, 0xde, 0x2f, 0x8a, 0x1e, 0x6b, 0x7b, 0x07, 0x72, 0x63, 0x09, 0xa4, 0x8e, 0xc0]),
    VP9: new Uint8Array([0x86, 0x00, 0x40, 0x92, 0x9c, 0x24, 0x48, 0x00, 0x00, 0x03, 0x70, 0x00, 0x00, 0x4b, 0x40])
  };
  private static readonly _surrogate28x16H264IFrame = surrogateH26428x16IFrame;
  private static readonly _surrogate28x16H264PFrame = surrogateH26428x16PFrame;
  private static readonly _surrogate28x16PFrameFrameNumberReset = 1;
  private static _surrogate28x16PFrameFrameNumber = SurrogateFrameDataManager._surrogate28x16PFrameFrameNumberReset;

  static getSurrogateAudioSilentPerCodec(codec: AudioCodec): ArrayBuffer | undefined {
    switch (codec) {
      case AudioCodec.OPUS:
        return SurrogateFrameDataManager._dummyAudioSilentPerCodec.OPUS.buffer;

      // TODO (AZ): Add a dummy AAC silent frame when AAC support is added
      case AudioCodec.AAC:
        return undefined;

      default:
        assertUnreachable(codec);
    }
  }

  static getInvalidSurrogateAudioData(): ArrayBuffer {
    return SurrogateFrameDataManager._singleZeroByte.buffer;
  }

  static getSurrogateVideoIFramePerCodec(codec: VideoCodec): ArrayBuffer | undefined {
    switch (codec) {
      case VideoCodec.H264:
        if (SurrogateFrameDataManager._isSafari) {
          return SurrogateFrameDataManager._surrogate28x16H264IFrame.buffer;
        }

        return SurrogateFrameDataManager._dummy2x2VideoIFramePerCodec.H264.buffer;

      case VideoCodec.VP8:
        return SurrogateFrameDataManager._dummy2x2VideoIFramePerCodec.VP8.buffer;

      case VideoCodec.VP9:
        return SurrogateFrameDataManager._dummy2x2VideoIFramePerCodec.VP9.buffer;

      default:
        assertUnreachable(codec);
    }
  }

  static getInvalidSurrogateVideoIFrame(codec: VideoCodec): SurrogateFrameType {
    if ((codec === VideoCodec.H264) && SurrogateFrameDataManager._isAndroidChrome) {
      SurrogateFrameDataManager._surrogate28x16PFrameFrameNumber = SurrogateFrameDataManager._surrogate28x16PFrameFrameNumberReset;

      return SkipFrame;
    }

    return SurrogateFrameDataManager._singleZeroByte.buffer;
  }

  static getSurrogateVideoPFramePerCodec(codec: VideoCodec): ArrayBuffer | undefined {
    switch (codec) {
      case VideoCodec.H264: {
        if (SurrogateFrameDataManager._isSafari) {
          return SurrogateFrameDataManager._surrogate28x16H264PFrame.buffer;
        }

        return SurrogateFrameDataManager._dummy2x2VideoPFramePerCodec.H264.buffer;
      }

      case VideoCodec.VP8:
        return SurrogateFrameDataManager._dummy2x2VideoPFramePerCodec.VP8.buffer;

      case VideoCodec.VP9:
        return SurrogateFrameDataManager._dummy2x2VideoPFramePerCodec.VP9.buffer;

      default:
        assertUnreachable(codec);
    }
  }

  static getInvalidSurrogateVideoPFrame(): SurrogateFrameType {
    return SurrogateFrameDataManager._singleZeroByte.buffer;
  }

  private constructor() {
    throw new Error('SurrogateFrameDataManager is a static class that may not be instantiated');
  }
}